import { Stack } from "expo-router";
// import { usePreventScreenCapture } from "expo-screen-capture";
import { StatusBar } from "expo-status-bar";
/* -----------------UI--------------- */
import { PaperProvider } from "react-native-paper";
import type { ThemeProp } from "react-native-paper/lib/typescript/types";

import theme from "@configs/theme";
import { useThemeContext } from "@contexts/ThemeContext";
import { ThemeProvider } from "expo-router/react-navigation";
import * as SecureStore from "expo-secure-store";
import "../src/helpers/lib/i18n";

import { API } from "@aws-amplify/api";
/* -----------------Types--------------- */
import { Auth } from "@aws-amplify/auth";
/* -----------------Helpers & Hooks--------------- */
import useCachedResources from "../src/hooks/useCachedResources";

/* -----------------Child components--------------- */
import { ThemeContextProvider } from "@contexts/ThemeContext";
import { ExamContextProvider } from "@features/exam";
import { buildType } from "@helpers/expo-extras";
import { AuthProvider } from "@memorang/applets";
import { identifyUser, initializeMixpanel } from "analytics";
import { GestureHandlerRootHOC } from "components/GestureHandlerRootHOC";

import { isWeb } from "@helpers/platform";
import { applyGlobalPolyfills, setUpAmplifyConfig } from "@memorang/helpers";
import { CommandBarProviderWrapper } from "components/CommandBarProviderWrapper";
import Head from "expo-router/head";

import { ClerkProvider } from "@clerk/clerk-expo";
import { getAwsExportsJson } from "@configs/awsexports";
import { isMockedData, mockedAppFavicon } from "@constants/demo";
import ReactQueryProvider from "@contexts/ReactQueryContext";
import NoInternetAccessContainer from "@features/dashboard/components/NoInternetAccessContainer";
import { PortalProvider } from "@gorhom/portal";
import { useAppStore } from "@hooks/useAppStore";
import useClerkUser from "@hooks/useClerkUser";
import { isDev } from "@memorang/constants";
import { TTSProvider } from "@memorang/features/src/TTS";
import { ToastProvider } from "@memorang/ui";
import { useEffect } from "react";

import { initSentry } from "@helpers/lib/sentry";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { LogBox } from "react-native"; // TODO: enable only for demos

// if (false) {
LogBox.ignoreAllLogs();
// }

// react-native LogBox is a no-op on web; the dev error toast comes from @expo/log-box
if (isDev && isWeb) {
	require("@expo/log-box/src/LogBox").default.ignoreAllLogs();
}

applyGlobalPolyfills();
initSentry();
initializeMixpanel();

const tokenCache = {
	async getToken(key: string) {
		try {
			const item = await SecureStore.getItemAsync(key);
			return item;
		} catch (error) {
			console.error("SecureStore get item error: ", error);
			await SecureStore.deleteItemAsync(key);
			return null;
		}
	},
	async saveToken(key: string, value: string) {
		try {
			return await SecureStore.setItemAsync(key, value);
		} catch (_err) {
			return;
		}
	},
};

function App() {
	const { isLoadingComplete, isDarkMode } = useCachedResources();

	const RenderRoot = () => {
		// usePreventScreenCapture();
		const { isDarkMode } = useThemeContext();

		const { currentExam } = useDemoExamStore();

		const demoExamId = currentExam?.id;

		const finalTheme = theme(isDarkMode, demoExamId);

		const { userLogicalId, isLoaded } = useClerkUser();

		const appId = useAppStore((store) => store.app?.id);
		const org = useAppStore((store) => store.companyName);
		const tenantId = useAppStore((store) => store.tenant?.id);
		const voiceId = useAppStore((state) => state.app?.elevenLabs?.voiceId);
		const appName = useAppStore((state) => state.app?.name);
		const setCurrentExam = useDemoExamStore((state) => state.setCurrentExam);

		useEffect(() => {
			if (isLoaded) {
				const awsExports = getAwsExportsJson();
				const headers = {
					"app-id": appId,
					"tenant-id": tenantId,
					"user-id": userLogicalId,
				};
				const config = setUpAmplifyConfig(awsExports, true, false, headers);

				Auth.configure(config);
				API.configure(config);
				setCurrentExam("proethica");
			}
		}, [appId, tenantId, userLogicalId, isLoaded, setCurrentExam]);

		if (!isLoaded) {
			return null;
		}

		return (
			<ExamContextProvider>
				<PaperProvider theme={finalTheme as unknown as ThemeProp}>
					<ThemeProvider
						value={{
							...finalTheme,
							// TODO: remove if react native paper gets a major version bump
							fonts: {
								...finalTheme.typography.fonts,
								heavy: finalTheme.typography.fonts.extraBold,
								// biome-ignore lint/correctness/noUndeclaredVariables: available in global namespace
							} as ReactNavigation.Theme["fonts"],
						}}
					>
						<StatusBar
							key={isDarkMode ? "dark" : "light"}
							style={isDarkMode ? "light" : "dark"}
						/>
						<ToastProvider>
							<CommandBarProviderWrapper>
								<TTSProvider
									org={org}
									tenantId={tenantId}
									appId={appId}
									appName={appName}
									voiceId={voiceId ?? ""}
								>
									<Stack
										screenOptions={{
											headerShown: false,
										}}
									/>
									<NoInternetAccessContainer />
								</TTSProvider>
							</CommandBarProviderWrapper>
						</ToastProvider>
					</ThemeProvider>
				</PaperProvider>
			</ExamContextProvider>
		);
	};

	const appKeys = useAppStore((store) => store.app?.keys);

	if (!isLoadingComplete) {
		return null;
	}

	const clerkPublishableKey = isDev
		? appKeys?.clerk?.publishableKeyDev
		: appKeys?.clerk?.publishableKeyProd;
	// const clerkPublishableKey = appKeys?.clerk?.publishableKeyDev;
	if (!clerkPublishableKey) {
		throw new Error("Clerk publishable key not found");
	}

	const shortcutIconHref = isMockedData
		? mockedAppFavicon
		: `https://icon.memorang.com/app-resources/${buildType}/ios/icon.png`;

	return (
		<>
			{isWeb && (
				<Head>
					<link rel="shortcut icon" href={shortcutIconHref} />
				</Head>
			)}
			<PortalProvider>
				<ClerkProvider
					tokenCache={tokenCache}
					publishableKey={clerkPublishableKey}
				>
					<GestureHandlerRootHOC>
						<ThemeContextProvider initIsDarkMode={isDarkMode}>
							<ReactQueryProvider>
								<AuthProvider app={buildType} identifyUser={identifyUser}>
									<RenderRoot />
								</AuthProvider>
							</ReactQueryProvider>
						</ThemeContextProvider>
					</GestureHandlerRootHOC>
				</ClerkProvider>
			</PortalProvider>
		</>
	);
}
export default App;
